import type React from "react";
import type { Book } from "../../../app/books";

import { ExternalLink, Users } from "lucide-react";
import { useTranslation } from "react-i18next";

type BookLinksProps = {
  book: Book;
};

export const BookLinks: React.FC<BookLinksProps> = ({ book }) => {
  const { t } = useTranslation();

  const bookshopUrl = book.isbn?.map((isbn) => getBookshopSearchUrl(isbn))?.find(() => true);
  const amazonUrl = book.isbn?.map((isbn) => getAmazonSearchUrl(isbn))?.find(() => true);
  const goodreadsUrl = book.isbn?.map((isbn) => `https://www.goodreads.com/search?q=${isbn}`)?.find(() => true);

  const urls = book.urls ?? [];

  return (
    <div className="dui-card bg-base-200 shadow-xl">
      <div className="dui-card-body">
        <h2 className="dui-card-title mb-4 flex items-center gap-2">
          <ExternalLink className="h-5 w-5" />
          {t("book.links.title")}
        </h2>

        <div className="space-y-4">
          {goodreadsUrl && (
            <a
              href={goodreadsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="dui-btn dui-btn-outline dui-btn-block"
            >
              <Users className="h-4 w-4" />
              {t("book.links.goodreads")}
            </a>
          )}

          {bookshopUrl && (
            <a
              href={bookshopUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="dui-btn dui-btn-outline dui-btn-block"
            >
              <ExternalLink className="h-4 w-4" />
              {t("book.links.bookshop")}
            </a>
          )}

          {amazonUrl && (
            <a
              href={amazonUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="dui-btn dui-btn-outline dui-btn-block"
            >
              <ExternalLink className="h-4 w-4" />
              {t("book.links.amazon")}
            </a>
          )}

          {urls.map((url) => (
            <a
              key={url.url}
              href={url.url}
              target="_blank"
              rel="noopener noreferrer"
              className="dui-btn dui-btn-outline dui-btn-block"
            >
              <ExternalLink className="h-4 w-4" />
              {url.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

function getAmazonSearchUrl(isbn: string): string {
  const locale = navigator.language.toLowerCase();

  const countryToTld: Record<string, string> = {
    us: "com",
    gb: "co.uk",
    uk: "co.uk", // fallback for UK
    de: "de",
    fr: "fr",
    ca: "ca",
    jp: "co.jp",
    au: "com.au",
    it: "it",
    es: "es",
    nl: "nl",
    in: "in",
    br: "com.br",
    mx: "com.mx"
  };

  // Extract country code from locale (e.g., 'en-GB' => 'gb')
  const countryCode = locale.split("-")[1]?.toLowerCase() || "us";
  const tld = countryToTld[countryCode] || "com"; // Default to US

  return `https://www.amazon.${tld}/s?k=${encodeURIComponent(isbn)}`;
}

function getBookshopSearchUrl(isbn: string): string {
  const locale = navigator.language.toLowerCase();

  // Map region to bookshop subdomain
  const countryToSubdomain: Record<string, string> = {
    us: "", // bookshop.org
    gb: "uk",
    uk: "uk",
    es: "cat", // Catalonia-focused
    cat: "cat"
  };

  const countryCode = locale.split("-")[1]?.toLowerCase() || "us";
  const subdomain = countryToSubdomain[countryCode] ?? "";

  const domain = subdomain ? `${subdomain}.bookshop.org` : `bookshop.org`;

  return `https://${domain}/books?keywords=${encodeURIComponent(isbn)}`;
}
